import * as React from 'react'
import { DatePicker } from '@/components/ui/date-time-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { TaxRateSummary } from '@/components/events/event-ticket-types'
import { TicketAudience } from '@/components/events/ticket-audience'
import { MaxPerOrderField, TicketCapacityField } from '@/components/events/ticket-capacity-field'

type TicketAudienceValue = 'everyone' | 'members' | 'specific_levels'

export function TicketTypeFormFields({
  audience,
  capacity,
  description,
  maxPerOrder,
  membershipLevelIds,
  name,
  price,
  salesClosesOn,
  salesOpensOn,
  taxRateId,
  taxRates,
  taxRatesState,
  onAudienceChange,
  onCapacityChange,
  onDescriptionChange,
  onLevelsChange,
  onMaxPerOrderChange,
  onNameChange,
  onPriceChange,
  onSalesClosesOnChange,
  onSalesOpensOnChange,
  onTaxRateChange,
}: {
  audience: TicketAudienceValue
  capacity: string
  description: string
  maxPerOrder: string
  membershipLevelIds: number[]
  name: string
  price: string
  salesClosesOn: string
  salesOpensOn: string
  taxRateId: string
  taxRates: TaxRateSummary[]
  taxRatesState: 'loading' | 'error' | 'ready'
  onAudienceChange: (value: TicketAudienceValue) => void
  onCapacityChange: (value: string) => void
  onDescriptionChange: (value: string) => void
  onLevelsChange: React.Dispatch<React.SetStateAction<number[]>>
  onMaxPerOrderChange: (value: string) => void
  onNameChange: (value: string) => void
  onPriceChange: (value: string) => void
  onSalesClosesOnChange: (value: string) => void
  onSalesOpensOnChange: (value: string) => void
  onTaxRateChange: (value: string) => void
}) {
  return (
    <>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_140px]">
        <TextField
          id="ticket-type-name"
          label="Ticket Type name"
          value={name}
          onChange={onNameChange}
        />
        <TextField
          id="ticket-type-price"
          inputMode="decimal"
          label="Ticket Type price"
          value={price}
          onChange={onPriceChange}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="ticket-type-description" className="text-caption text-muted-foreground">
          Ticket Type description
        </Label>
        <Textarea
          id="ticket-type-description"
          rows={2}
          className="min-h-0"
          value={description}
          onChange={(event) => onDescriptionChange(event.target.value)}
        />
      </div>
      <SalesWindowFields
        opensOn={salesOpensOn}
        closesOn={salesClosesOn}
        maxPerOrder={maxPerOrder}
        capacity={capacity}
        onOpensOnChange={onSalesOpensOnChange}
        onClosesOnChange={onSalesClosesOnChange}
        onMaxPerOrderChange={onMaxPerOrderChange}
        onCapacityChange={onCapacityChange}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <TicketAudience
          audience={audience}
          membershipLevelIds={membershipLevelIds}
          onAudienceChange={onAudienceChange}
          onLevelsChange={onLevelsChange}
        />
        <TaxRateField
          value={taxRateId}
          rates={taxRates}
          state={taxRatesState}
          onChange={onTaxRateChange}
        />
      </div>
    </>
  )
}

function TaxRateField({
  value,
  rates,
  state,
  onChange,
}: {
  value: string
  rates: TaxRateSummary[]
  state: 'loading' | 'error' | 'ready'
  onChange: (value: string) => void
}) {
  if (state === 'loading')
    return <p className="text-caption text-muted-foreground">Loading Tax Rates…</p>
  if (state === 'error') return <Alert>Tax Rates could not be loaded. Try again.</Alert>

  return (
    <div className="space-y-1">
      <Label htmlFor="ticket-type-tax-rate" className="text-caption text-muted-foreground">
        Tax rate
      </Label>
      <Select
        value={value || 'none'}
        onValueChange={(next) => onChange(next === 'none' ? '' : next)}
      >
        <SelectTrigger id="ticket-type-tax-rate" aria-label="Tax rate" className="h-8 w-full">
          <SelectValue placeholder="No Tax Rate" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No Tax Rate</SelectItem>
          {rates.map((rate) => (
            <SelectItem key={rate.id} value={String(rate.id)}>
              {rate.name} · {rate.percentage}%
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function TextField({
  id,
  inputMode,
  label,
  value,
  onChange,
}: {
  id: string
  inputMode?: 'decimal'
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-caption text-muted-foreground">
        {label}
      </Label>
      <Input
        id={id}
        className="h-8"
        inputMode={inputMode}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

function SalesWindowFields({
  opensOn,
  closesOn,
  maxPerOrder,
  capacity,
  onOpensOnChange,
  onClosesOnChange,
  onMaxPerOrderChange,
  onCapacityChange,
}: {
  opensOn: string
  closesOn: string
  maxPerOrder: string
  capacity: string
  onOpensOnChange: (value: string) => void
  onClosesOnChange: (value: string) => void
  onMaxPerOrderChange: (value: string) => void
  onCapacityChange: (value: string) => void
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_1fr_110px_110px]">
      <SalesWindowField label="Sales window from">
        <DatePicker
          id="ticket-type-sales-opens-on"
          label="Sales window from"
          value={opensOn}
          onChange={onOpensOnChange}
        />
      </SalesWindowField>
      <SalesWindowField label="Sales window to">
        <DatePicker
          id="ticket-type-sales-closes-on"
          label="Sales window to"
          value={closesOn}
          onChange={onClosesOnChange}
        />
      </SalesWindowField>
      <TicketCapacityField value={capacity} onChange={onCapacityChange} />
      <MaxPerOrderField value={maxPerOrder} onChange={onMaxPerOrderChange} />
    </div>
  )
}

function SalesWindowField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="mb-1 block text-caption text-muted-foreground">{label}</Label>
      {children}
    </div>
  )
}
